import React, { useImperativeHandle, forwardRef } from 'react';
import { useForm, Control, FieldErrors, SubmitHandler } from 'react-hook-form';
import { Form, Question, FormAnswers } from '../../api/types/forms';
import { Typography, Box, Paper, Grid } from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

import DynamicFormQuestion from './DynamicFormQuestion';
import ForEachQuestionRenderer from './ForEachQuestionRenderer';
import { getInitialFormValues, convertFormDataToAnswers, evaluateDisplayLogic } from './utils/formUtils';

interface DynamicFormRendererProps {
  formDefinition: Form;
  initialAnswers?: FormAnswers | null;
  onSubmit: (answers: FormAnswers) => void;
  onCancel?: () => void;
  organizationId?: string;
  programId?: string;
  inspectionProgramId?: string;
  itemId?: string;
  onUploadingChange?: (isUploading: boolean) => void;
  onChange?: (answers: FormAnswers) => void;
}

const DynamicFormRenderer = forwardRef<({ submit: () => void }), DynamicFormRendererProps>((
  { formDefinition, initialAnswers, onSubmit, organizationId, programId, inspectionProgramId, itemId, onUploadingChange, onChange },
  ref
) => {
  const { control, handleSubmit, formState: { errors: formErrors }, watch } = useForm<{ [key: string]: any }>({
    defaultValues: getInitialFormValues(formDefinition.questions, initialAnswers),
    resolver: async (data) => ({ values: data, errors: {} }), // Simple pass-through resolver, actual validation is per-field rules
    mode: 'onChange',
    shouldUnregister: false // Critical for preserving split-array data
  });

  // NOTE: Removed useEffect that reset form when initialAnswers changed.
  // This was causing infinite loops because reset() triggers watch(), which cascades updates.
  // The initial values are already properly set via defaultValues in useForm,
  // and the parent component (InspectionRowDetailsModal) handles initialization correctly.

  // Uploading State Management
  const [uploadingStates, setUploadingStates] = React.useState<Record<string, boolean>>({});
  const handleUploadingChange = React.useCallback((fieldName: string, isUploading: boolean) => {
    setUploadingStates(prev => {
      if (prev[fieldName] === isUploading) return prev;
      return { ...prev, [fieldName]: isUploading };
    });
  }, []);

  const isAnyFileUploading = Object.values(uploadingStates).some(status => status);

  // Store callbacks in refs to avoid dependency issues
  const onUploadingChangeRef = React.useRef(onUploadingChange);
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onUploadingChangeRef.current = onUploadingChange;
    onChangeRef.current = onChange;
  });

  // Track last emitted uploading state to avoid unnecessary calls
  const lastEmittedUploadingRef = React.useRef<boolean | null>(null);
  React.useEffect(() => {
    if (lastEmittedUploadingRef.current !== isAnyFileUploading) {
      lastEmittedUploadingRef.current = isAnyFileUploading;
      onUploadingChangeRef.current?.(isAnyFileUploading);
    }
  }, [isAnyFileUploading]);


  // Change Detection - disabled for now to avoid infinite loops
  // The modal uses imperative submit() which triggers onSubmit directly
  // So onChange tracking is not needed for this use case
  const formValues = watch();


  // NOTE: This useEffect was causing infinite re-renders because formValues
  // is a new object reference on every render from watch().
  // Since the modal uses imperative submit() via ref, onChange is not used.
  // Commenting out to fix the infinite loop issue.
  /*
  React.useEffect(() => {
    // Sync Check: Check for inconsistent state where FOR_EACH source has values but target field array is not yet populated
    const isSyncingFieldArrays = formDefinition.questions.some(q => {
      if (q.display_logic.condition_type === 'for_each' && q.display_logic.source_multiselect_id) {
        const sourceQId = q.display_logic.source_multiselect_id;
        const sourceName = `question_${sourceQId}`;
        const sourceValue = formValues[sourceName];

        const sourceQ = formDefinition.questions.find(sq => sq.id === sourceQId);

        if (sourceQ && sourceQ.type === 'multiselect') {
          // Split array strategy
          const selectedValues = Array.isArray(sourceValue) ? sourceValue : [];
          if (selectedValues.length === 0) return false;

          // If we have selected values, each must have a corresponding initialized field array (at least 1 item)
          return selectedValues.some((val: string) => {
            const splitKey = `forEachFields_${sourceQId}_${val}_${q.id}`;
            const targetValue = formValues[splitKey];
            const targetCount = Array.isArray(targetValue) ? targetValue.length : 0;
            return targetCount === 0;
          });
        } else {
          // Standard strategy
          const targetName = `forEachFields_${q.id}`;
          const targetValue = formValues[targetName];
          const sourceCount = Array.isArray(sourceValue) ? sourceValue.length : 0;
          const targetCount = Array.isArray(targetValue) ? targetValue.length : 0;
          return sourceCount > 0 && targetCount === 0;
        }
      }
      return false;
    });

    if (isSyncingFieldArrays) return;

    const answers = convertFormDataToAnswers(formValues, formDefinition, initialAnswers);
    const currentAnswersJson = JSON.stringify(answers);
    const lastAnswersJson = lastEmittedAnswersRef.current ? JSON.stringify(lastEmittedAnswersRef.current) : null;

    if (currentAnswersJson !== lastAnswersJson) {
      lastEmittedAnswersRef.current = answers;
      onChangeRef.current?.(answers);
    }
  }, [formValues, formDefinition, initialAnswers]);
  */


  // Rendering Helpers
  const renderSingleQuestionInstance = React.useCallback((
    question: Question,
    fieldName: string,
    currentControl: Control<any>,
    currentFormErrors: FieldErrors<any>,
    labelOverride?: string,
    rulesOverride?: Record<string, any>
  ) => {
    return (
      <DynamicFormQuestion
        question={question}
        fieldName={fieldName}
        control={currentControl}
        errors={currentFormErrors}
        labelOverride={labelOverride}
        rulesOverride={rulesOverride}
        organizationId={organizationId}
        programId={programId}
        inspectionProgramId={inspectionProgramId}
        itemId={itemId}
        isAnyFileUploading={isAnyFileUploading}
        onUploadingChange={handleUploadingChange}
      />
    );
  }, [organizationId, programId, inspectionProgramId, itemId, isAnyFileUploading, handleUploadingChange]);

  const onSubmitHandler: SubmitHandler<{ [key: string]: any }> = (data) => {
    const answers = convertFormDataToAnswers(data, formDefinition, initialAnswers);
    onSubmit(answers);
  };

  useImperativeHandle(ref, () => ({
    submit: () => handleSubmit(onSubmitHandler)(),
  }), [handleSubmit, onSubmitHandler, formDefinition, initialAnswers]);


  // Group ALWAYS questions without children for multi-column layout
  const groupedQuestions: (Question | Question[])[] = [];
  let currentAlwaysGroup: Question[] = [];

  formDefinition.questions.forEach((question) => {
    const shouldDisplay = evaluateDisplayLogic(question.display_logic, formValues);
    if (!shouldDisplay) return;

    // Check if this question has FOR_EACH children
    const hasForEachChildren = question.type === 'multiselect' && formDefinition.questions.some(childQ =>
      childQ.display_logic.condition_type === 'for_each' &&
      (childQ.display_logic.source_multiselect_id === question.id || childQ.display_logic.source_multiselect_id === question.uuid)
    );

    const isAlwaysWithoutChildren = question.display_logic.condition_type === 'always' && !hasForEachChildren;
    const isTextOrTextarea = question.type === 'text' || question.type === 'textarea';
    const isForEach = question.display_logic.condition_type === 'for_each';

    if (isAlwaysWithoutChildren && !isTextOrTextarea && !isForEach) {
      currentAlwaysGroup.push(question);
    } else {
      if (currentAlwaysGroup.length > 0) {
        groupedQuestions.push(currentAlwaysGroup);
        currentAlwaysGroup = [];
      }
      groupedQuestions.push(question);
    }
  });
  if (currentAlwaysGroup.length > 0) groupedQuestions.push(currentAlwaysGroup);


  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <form onSubmit={handleSubmit(onSubmitHandler)} style={{ width: '100%' }}>
        {formDefinition.description && (
          <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>{formDefinition.description}</Typography>
        )}

        {groupedQuestions.map((item, index) => {
          if (Array.isArray(item)) {
            // Render Grid of Questions
            const groupCount = item.length;
            let gridCols: { xs: number; sm: number; md: number } = { xs: 12, sm: 6, md: 4 };
            if (groupCount === 1) gridCols = { xs: 12, sm: 12, md: 12 };
            else if (groupCount === 2) gridCols = { xs: 12, sm: 6, md: 6 };

            return (
              <Grid container spacing={2} key={`always-group-${index}`} sx={{ mb: 2, display: 'flex', alignItems: 'stretch' }}>
                {item.map((question) => (
                  <Grid item xs={gridCols.xs} sm={gridCols.sm} md={gridCols.md} key={question.id} sx={{ display: 'flex', alignItems: 'stretch', minHeight: 0 }}>
                    <Paper elevation={2} sx={{ p: 2.5, width: '100%', border: '1px solid #e0e0e0', borderRadius: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
                        {renderSingleQuestionInstance(question, `question_${question.id}`, control, formErrors)}
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            );
          } else {
            // Render Single Item (Question or FOR_EACH group or Parent w/ Children)
            const question = item as Question;
            const isForEach = question.display_logic.condition_type === 'for_each' && question.display_logic.source_multiselect_id;
            const hasForEachChildren = question.type === 'multiselect' &&
              formDefinition.questions.some(childQ =>
                childQ.display_logic.condition_type === 'for_each' &&
                (childQ.display_logic.source_multiselect_id === question.id || childQ.display_logic.source_multiselect_id === question.uuid)
              );

            if (isForEach) {
              // Check if nested (skip if root) - Logic here matches original renderer logic
              // If source is multiselect, it's a split array rendered inside the multiselect's group?
              // No, if `isForEach` is true, it means THIS question repeats.
              // "Nested" check in original code:
              const isNestedForEach = formDefinition.questions.some(parentQ =>
                (parentQ.display_logic.condition_type === 'for_each' || parentQ.type === 'multiselect') &&
                formDefinition.questions.some(childQ =>
                  childQ.display_logic.condition_type === 'for_each' &&
                  (childQ.display_logic.source_multiselect_id === parentQ.id || childQ.display_logic.source_multiselect_id === parentQ.uuid) &&
                  childQ.id === question.id
                )
              );
              if (isNestedForEach) return null; // Logic is handled by parent

              // Render Root For Each
              // If source is multiselect, we actually need to render it specially if the original design dictates.
              // In original code: if question depends on Multiselect, it renders a ForEachQuestionRenderer block.

              const sourceQuestionRHFName = `question_${question.display_logic.source_multiselect_id}`;
              let watchedSourceValue = formValues[sourceQuestionRHFName];
              if (!Array.isArray(watchedSourceValue)) {
                watchedSourceValue = (typeof watchedSourceValue === 'string' && watchedSourceValue) ? [watchedSourceValue] : [];
              }
              const fieldArrayName = `forEachFields_${question.id}`;

              return (
                <Box key={`${question.id}-foreach-group`} mb={2}>
                  <ForEachQuestionRenderer
                    control={control}
                    errors={formErrors}
                    question={question}
                    sourceQuestionValue={watchedSourceValue}
                    parentFieldNamePrefix={fieldArrayName}
                    initialAnswers={initialAnswers}
                    allQuestions={formDefinition.questions}
                    renderSingleQuestionInstance={renderSingleQuestionInstance}
                  />
                </Box>
              );
            }

            // Standard Question (or Parent Multiselect)
            const fieldName = `question_${question.id}`;
            const isParentQuestion = hasForEachChildren;
            const selectedValues = (hasForEachChildren && Array.isArray(formValues[fieldName])) ? formValues[fieldName] : [];
            const forEachChildren = hasForEachChildren ? formDefinition.questions.filter(childQ =>
              childQ.display_logic.condition_type === 'for_each' &&
              (childQ.display_logic.source_multiselect_id === question.id || childQ.display_logic.source_multiselect_id === question.uuid)
            ) : [];

            return (
              <Box key={question.id} mb={2}>
                <Paper elevation={2} sx={{ p: 2.5, mb: 3, borderLeft: isParentQuestion ? '4px solid #1976d2' : '1px solid #e0e0e0', border: '1px solid #e0e0e0', borderRadius: 2 }}>
                  {renderSingleQuestionInstance(question, fieldName, control, formErrors)}
                </Paper>

                {/* Render Children Groups for Multiselect */}
                {hasForEachChildren && selectedValues.length > 0 && forEachChildren.length > 0 && (
                  <Box mt={3}>
                    {selectedValues.map((selectedValue: string, valueIndex: number) => {
                      // Use type-safe comparison to find the label
                      let optionLabel = question.options?.find(opt => opt.value === selectedValue)?.label;
                      if (!optionLabel) {
                        optionLabel = question.options?.find(opt => String(opt.value) === String(selectedValue))?.label || String(selectedValue);
                      }
                      return (
                        <Box key={`${question.id}-group-${selectedValue}-${valueIndex}`} mt={valueIndex > 0 ? 3 : 0}
                          style={{ paddingLeft: '20px', marginLeft: '64px', borderLeft: '5px solid #1976d2', position: 'relative', backgroundColor: '#f5f7fa', borderRadius: '0 6px 6px 0', paddingTop: '20px', paddingBottom: '20px', paddingRight: '20px', border: '2px solid #e3f2fd' }}>
                          <ArrowRightIcon sx={{ position: 'absolute', left: '-12px', top: '-12px', color: '#1976d2', fontSize: '24px', zIndex: 2, backgroundColor: '#ffffff', borderRadius: '50%', padding: '2px' }} />
                          <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#1565c0', marginBottom: '24px', paddingBottom: '12px', borderBottom: '2px solid #bbdefb' }}>
                            {optionLabel}
                          </Typography>

                          {/* Render Children for this Option */}
                          <Box>
                            {(() => {
                              const textChildren: { question: Question; element: JSX.Element }[] = [];
                              const gridChildren: { question: Question; element: JSX.Element }[] = [];

                              forEachChildren.forEach(childQuestion => {
                                const childSourceValue = [selectedValue]; // The split array logic forces simple [value] array
                                // Use question.id (stringId) for field name generation
                                const childFieldArrayName = `forEachFields_${question.id}_${selectedValue}_${childQuestion.id}`;

                                const childElement = (
                                  <Box key={`${childQuestion.id}-${selectedValue}`} sx={{ height: '100%', display: 'flex', flexDirection: 'column', '& > *': { height: '100%' } }}>
                                    <ForEachQuestionRenderer
                                      control={control}
                                      errors={formErrors}
                                      question={childQuestion}
                                      sourceQuestionValue={childSourceValue}
                                      parentFieldNamePrefix={childFieldArrayName}
                                      initialAnswers={initialAnswers}
                                      allQuestions={formDefinition.questions}
                                      renderSingleQuestionInstance={renderSingleQuestionInstance}
                                      nestingLevel={1}
                                    />
                                  </Box>
                                );

                                if (childQuestion.type === 'text' || childQuestion.type === 'textarea') {
                                  textChildren.push({ question: childQuestion, element: childElement });
                                } else {
                                  gridChildren.push({ question: childQuestion, element: childElement });
                                }
                              });

                              // Grid Logic
                              const gridCount = gridChildren.length;
                              let gridCols: { xs: number; sm: number; md: number } = { xs: 12, sm: 6, md: 4 };
                              if (gridCount === 1) gridCols = { xs: 12, sm: 12, md: 12 };
                              else if (gridCount === 2) gridCols = { xs: 12, sm: 6, md: 6 };

                              return (
                                <>
                                  {textChildren.map(({ element }, idx) => <Box key={`text-${idx}`}>{element}</Box>)}
                                  {gridChildren.length > 0 && (
                                    <Grid container spacing={2} sx={{ mt: textChildren.length > 0 ? 2 : 0, display: 'flex', alignItems: 'stretch' }}>
                                      {gridChildren.map(({ element }, idx) => (
                                        <Grid item xs={gridCols.xs} sm={gridCols.sm} md={gridCols.md} key={`grid-${idx}`} sx={{ display: 'flex', alignItems: 'stretch', minHeight: 0 }}>
                                          <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>{element}</Box>
                                        </Grid>
                                      ))}
                                    </Grid>
                                  )}
                                </>
                              );
                            })()}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            );
          }
        })}
      </form>
    </LocalizationProvider>
  );
});

DynamicFormRenderer.displayName = 'DynamicFormRenderer';

export default DynamicFormRenderer;